import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Check, ChevronDown, ChevronUp, ClipboardCheck, Loader2, MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ContractorQualityChecklist({ contractor, projectId }) {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [noteInputs, setNoteInputs] = useState({});

  const isGeneralContractor = contractor?.contractor_hierarchy === 'general_contractor';

  useEffect(() => {
    if (contractor?.id) fetchChecklists();
  }, [contractor?.id, projectId]);

  const fetchChecklists = async () => {
    setLoading(true);
    try {
      let res;
      if (isGeneralContractor && projectId) {
        // General contractors see ALL checklists for the project
        res = await axios.get(`${API}/quality-checklists/project/${projectId}`);
      } else {
        // Subcontractors see only their own assigned checklists
        res = await axios.get(`${API}/quality-checklists/contractor/${contractor.id}`);
      }
      setChecklists(res.data.checklists || []);
    } catch (err) {
      console.error('Error fetching checklists:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = async (checklistId, itemId, checked) => {
    const key = `${checklistId}-${itemId}`;
    setSubmitting(key);
    try {
      const notes = noteInputs[key] || '';
      const res = await axios.post(`${API}/quality-checklists/${checklistId}/check`, {
        item_id: itemId,
        role: 'contractor',
        checked,
        notes
      });
      setChecklists(prev => prev.map(c => c.id === checklistId ? res.data.checklist : c));
      if (checked) toast.success('Item checked');
    } catch (err) {
      console.error('Error checking item:', err);
      toast.error('Failed to update item');
    } finally {
      setSubmitting(null);
    }
  };

  const getProgress = (checklist) => {
    const total = checklist.items.length;
    const done = checklist.items.filter(i => i.contractor_checked).length;
    return { done, total, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
  };

  const statusColor = (status) => {
    if (status === 'completed') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status === 'in_progress') return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    return 'bg-[#2A2A2A] text-[#8A8A8A] border-[#3A3A3A]';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#BC5D41]" />
      </div>
    );
  }

  if (checklists.length === 0) {
    return (
      <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
        <CardContent className="py-12 text-center">
          <ClipboardCheck className="w-12 h-12 text-[#3A3A3A] mx-auto mb-4" />
          <p className="text-[#8A8A8A]">No quality checklists assigned</p>
          <p className="text-sm text-[#5A5A5A] mt-1">Checklists will appear here when assigned to you</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="quality-checklists">
      <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
        <CardHeader>
          <CardTitle className="text-[#E5E2DD]">Quality Checklists</CardTitle>
          <CardDescription className="text-[#8A8A8A]">
            {isGeneralContractor
              ? 'All trade checklists for this project. Goal: zero punch list items.'
              : 'Complete each item before final inspection. Goal: zero punch list items.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {checklists.map(checklist => {
            const { done, total, pct } = getProgress(checklist);
            const isExpanded = expandedId === checklist.id;
            const categories = [...new Set(checklist.items.map(i => i.category))];

            return (
              <div key={checklist.id} className="rounded-xl border border-[#2A2A2A] overflow-hidden" data-testid={`checklist-${checklist.trade_code}`}>
                {/* Header */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : checklist.id)}
                  className="w-full flex items-center justify-between p-4 bg-[#0D0D0D] hover:bg-[#151515] transition-colors"
                  data-testid={`checklist-toggle-${checklist.trade_code}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#70170F]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-[#BC5D41]">{checklist.trade_code}</span>
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-sm font-semibold text-[#E5E2DD] truncate">{checklist.trade_name}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <Badge className={`text-[10px] px-1.5 py-0 ${statusColor(checklist.status)}`}>
                          {checklist.status.replace('_', ' ')}
                        </Badge>
                        <span className="text-xs text-[#5A5A5A]">{done}/{total}</span>
                        {isGeneralContractor && checklist.contractor_name && (
                          <span className="text-[10px] text-[#8A8A8A]">{checklist.contractor_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Progress ring */}
                    <div className="relative w-9 h-9">
                      <svg className="w-9 h-9 -rotate-90">
                        <circle cx="18" cy="18" r="14" fill="none" stroke="#2A2A2A" strokeWidth="3" />
                        <circle cx="18" cy="18" r="14" fill="none" stroke={pct === 100 ? '#22c55e' : '#BC5D41'} strokeWidth="3"
                          strokeDasharray={`${(pct / 100) * 88} 88`} strokeLinecap="round" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-[#E5E2DD]">{pct}%</span>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-[#5A5A5A]" /> : <ChevronDown className="w-4 h-4 text-[#5A5A5A]" />}
                  </div>
                </button>

                {/* Expanded items */}
                {isExpanded && (
                  <div className="border-t border-[#2A2A2A]">
                    {categories.map(cat => (
                      <div key={cat}>
                        <div className="px-4 py-2 bg-[#111111]">
                          <p className="text-[10px] font-semibold text-[#BC5D41] uppercase tracking-wider">{cat}</p>
                        </div>
                        {checklist.items.filter(i => i.category === cat).map(item => {
                          const key = `${checklist.id}-${item.id}`;
                          const isSubmitting = submitting === key;
                          return (
                            <div key={item.id} className="px-4 py-3 border-b border-[#1A1A1A] last:border-0" data-testid={`item-${item.id}`}>
                              <div className="flex items-start gap-3">
                                {/* Checkbox */}
                                <button
                                  onClick={() => handleCheck(checklist.id, item.id, !item.contractor_checked)}
                                  disabled={isSubmitting}
                                  className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                                    item.contractor_checked
                                      ? 'bg-green-600 border-green-600'
                                      : 'border-[#3A3A3A] hover:border-[#BC5D41]'
                                  }`}
                                  data-testid={`check-${item.id}`}
                                >
                                  {isSubmitting ? (
                                    <Loader2 className="w-3 h-3 animate-spin text-white" />
                                  ) : item.contractor_checked ? (
                                    <Check className="w-3.5 h-3.5 text-white" />
                                  ) : null}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${item.contractor_checked ? 'text-[#5A5A5A] line-through' : 'text-[#E5E2DD]'}`}>
                                    {item.text}
                                  </p>
                                  {/* Admin check indicator */}
                                  {item.admin_checked && (
                                    <span className="text-[10px] text-green-400 mt-1 inline-block">Verified by admin</span>
                                  )}
                                  {/* Notes input */}
                                  {item.contractor_notes && (
                                    <p className="text-xs text-[#8A8A8A] mt-1 flex items-center gap-1">
                                      <MessageSquare className="w-3 h-3" /> {item.contractor_notes}
                                    </p>
                                  )}
                                  {!item.contractor_checked && (
                                    <Input
                                      placeholder="Add note (optional)"
                                      value={noteInputs[key] || ''}
                                      onChange={e => setNoteInputs(prev => ({ ...prev, [key]: e.target.value }))}
                                      className="mt-2 h-8 text-xs bg-[#0D0D0D] border-[#2A2A2A] text-[#E5E2DD] placeholder:text-[#3A3A3A]"
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
